import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { 
  BarChart3, 
  Users, 
  UserPlus, 
  Network, 
  Calculator, 
  Grid3x3, 
  Settings,
  HelpCircle,
  FileText,
  Briefcase,
  Car,
  Wallet,
  Wrench,
  Target,
  Gauge,
  Bell,
  Upload,
  Building2,
  FileSpreadsheet,
  Clock,
  Euro,
  Award,
  Shirt,
  Key,
  FolderOpen,
  TrendingUp,
  ClipboardList,
  UserCheck,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Star,
  CheckCircle2,
  Info,
  Zap,
  User
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";

interface HelpSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  description: string;
  subsections: {
    title: string;
    content: string;
    tips?: string[];
  }[];
}

// Help sections for employees - only profile-related content
const employeeHelpSections: HelpSection[] = [
  {
    id: "my-profile",
    title: "Mein Profil",
    icon: <User className="h-5 w-5" />,
    description: "Übersicht über Ihre persönlichen Daten und Arbeitskleidung.",
    subsections: [
      {
        title: "Stammdaten einsehen",
        content: "Hier sehen Sie Ihre persönlichen Informationen: Name, Geburtsdatum, Adresse, Kontaktdaten, Notfallkontakt und Bankverbindung. Diese Daten werden von der Personalabteilung gepflegt.",
        tips: [
          "Bei Änderungswünschen wenden Sie sich an die Personalabteilung",
          "Prüfen Sie regelmäßig, ob Ihre Daten aktuell sind"
        ]
      },
      {
        title: "Vertragsdaten",
        content: "Übersicht Ihrer Arbeitsvertragsinformationen: Eintrittsdatum, Vertragsart, Wochenarbeitszeit, Urlaubstage und Abteilungszuordnung."
      },
      {
        title: "Arbeitskleidung",
        content: "Hier können Sie Ihre Konfektionsgrößen für die Arbeitskleidung hinterlegen: T-Shirt, Sweatshirt, Fleecejacke, Winterjacke, Hose und Sicherheitsschuhe.",
        tips: [
          "Wählen Sie die passende Größe aus der Liste oder geben Sie eine individuelle Größe ein",
          "Klicken Sie auf 'Speichern' um Ihre Größen zu hinterlegen",
          "Ihre Größen werden für zukünftige Bestellungen verwendet"
        ]
      },
      {
        title: "Stellenbeschreibung",
        content: "Informationen zu Ihrer Position und Ihren Aufgabenbereichen. Diese Beschreibung wird von Ihrem Vorgesetzten gepflegt."
      }
    ]
  },
  {
    id: "my-finances",
    title: "Meine Finanzdaten",
    icon: <Euro className="h-5 w-5" />,
    description: "Einsicht in Ihre Gehaltsdaten, Boni und Benefits (nur Ansicht).",
    subsections: [
      {
        title: "Gehaltsverlauf",
        content: "Hier sehen Sie die Historie Ihrer Gehaltsänderungen: Datum, Betrag, Gehaltsart und ggf. der Änderungsgrund.",
        tips: [
          "Die Daten sind nur zur Ansicht verfügbar",
          "Bei Fragen wenden Sie sich an die Personalabteilung"
        ]
      },
      {
        title: "Benefits & Zusatzleistungen",
        content: "Übersicht Ihrer Zusatzleistungen wie Firmenwagen, Jobticket, betriebliche Altersvorsorge etc. mit Start- und Enddatum sowie monatlichem Wert."
      },
      {
        title: "Bonuszahlungen",
        content: "Historie Ihrer Einmalzahlungen und Boni: Weihnachtsgeld, Prämien, Provisionen mit Datum, Betrag und Beschreibung."
      }
    ]
  },
  {
    id: "my-costs",
    title: "Meine Kosten",
    icon: <Car className="h-5 w-5" />,
    description: "Einsicht in zugeordnete Fahrzeugkosten und sonstige Kosten.",
    subsections: [
      {
        title: "Fahrzeugkosten",
        content: "Falls Ihnen ein Firmenfahrzeug zugeordnet ist, sehen Sie hier die damit verbundenen Kosten: Leasing, Versicherung, Tankkarte (DKV) und Wartung."
      },
      {
        title: "Sonstige Kosten",
        content: "Weitere Ihnen zugeordnete Kosten wie Schulungen, Arbeitskleidung, IT-Ausstattung oder Werkzeuge."
      }
    ]
  },
  {
    id: "my-equipment",
    title: "Meine Ausstattung",
    icon: <Wrench className="h-5 w-5" />,
    description: "Übersicht über Ihre zugewiesenen Werkzeuge und Geräte.",
    subsections: [
      {
        title: "Werkzeuge & Geräte",
        content: "Liste der Ihnen zugewiesenen Werkzeuge und Geräte mit Seriennummer, Übergabedatum und Wiederbeschaffungswert.",
        tips: [
          "Prüfen Sie regelmäßig, ob die Liste aktuell ist",
          "Melden Sie fehlende oder defekte Werkzeuge Ihrem Vorgesetzten"
        ]
      },
      {
        title: "Zugangsdaten",
        content: "Ihre externen Zugangsdaten für Systeme wie Baustellenportale oder Lieferanten-Accounts (verschlüsselt gespeichert)."
      }
    ]
  },
  {
    id: "my-documents",
    title: "Meine Dokumente",
    icon: <FolderOpen className="h-5 w-5" />,
    description: "Zugriff auf Ihre hinterlegten Dokumente.",
    subsections: [
      {
        title: "Dokumentenablage",
        content: "Hier finden Sie Ihre hinterlegten Dokumente wie Arbeitsverträge, Zeugnisse, Bescheinigungen und Führerscheine.",
        tips: [
          "Klicken Sie auf ein Dokument für die Vorschau",
          "PDF-Dokumente werden direkt im Browser angezeigt",
          "Dokumente können heruntergeladen werden"
        ]
      }
    ]
  },
  {
    id: "my-evaluations",
    title: "Meine Bewertungen",
    icon: <Star className="h-5 w-5" />,
    description: "Einsicht in Ihre Leistungsbewertungen.",
    subsections: [
      {
        title: "Bewertungshistorie",
        content: "Übersicht Ihrer monatlichen Leistungsbewertungen. Jedes Kriterium wird einzeln bewertet und kann kommentiert sein.",
        tips: [
          "Grün = Voll erfüllt, Gelb = Erfüllt, Orange = Teilweise, Rot = Nicht erfüllt",
          "Bei Fragen zu Ihrer Bewertung wenden Sie sich an Ihren Vorgesetzten"
        ]
      }
    ]
  }
];

// Full help sections for admins/HR/managers
const adminHelpSections: HelpSection[] = [
  {
    id: "dashboard",
    title: "Dashboard",
    icon: <BarChart3 className="h-5 w-5" />,
    description: "Übersicht über alle wichtigen Kennzahlen und den aktuellen Status Ihrer Mitarbeiter.",
    subsections: [
      {
        title: "Statistik-Übersicht",
        content: "Das Dashboard zeigt Ihnen auf einen Blick die wichtigsten Kennzahlen: Anzahl der Mitarbeiter, durchschnittliche Bewertungen, offene Bewerber und anstehende Aufgaben.",
        tips: [
          "Klicken Sie auf eine Statistik-Karte, um direkt zum entsprechenden Bereich zu gelangen",
          "Die Daten werden automatisch aktualisiert"
        ]
      },
      {
        title: "Aktuelle Bewertungen",
        content: "Sehen Sie die neuesten Mitarbeiterbewertungen und deren Leistungsniveau. Farbcodierung zeigt sofort, ob die Leistung über oder unter den Erwartungen liegt.",
      },
      {
        title: "Schnellzugriff",
        content: "Von hier aus können Sie schnell zu den am häufigsten genutzten Funktionen navigieren."
      }
    ]
  },
  {
    id: "employees",
    title: "Mitarbeiter",
    icon: <Users className="h-5 w-5" />,
    description: "Verwaltung aller Mitarbeiterdaten, von Stammdaten bis zu Bewertungen.",
    subsections: [
      {
        title: "Mitarbeiterliste",
        content: "Übersicht aller aktiven Mitarbeiter mit Suchfunktion und Filteroptionen nach Abteilung, Position oder Status.",
        tips: [
          "Nutzen Sie die Suchleiste für schnelles Finden",
          "Klicken Sie auf einen Mitarbeiter für die Detailansicht"
        ]
      },
      {
        title: "Stammdaten",
        content: "Persönliche Informationen des Mitarbeiters: Name, Geburtsdatum, Adresse, Kontaktdaten, Notfallkontakt, Bankverbindung, Steuer-ID und Sozialversicherungsnummer.",
        tips: [
          "Profilbild kann hochgeladen werden",
          "Alle Änderungen werden protokolliert"
        ]
      },
      {
        title: "Vertragsdaten",
        content: "Arbeitsvertragsinformationen: Eintrittsdatum, Vertragsart (befristet/unbefristet), Wochenarbeitszeit, Urlaubstage und Abteilungszuordnung."
      },
      {
        title: "Gehaltsverlauf",
        content: "Historie aller Gehaltsänderungen mit Datum, Betrag, Gehaltsart (Stunden-/Monatslohn) und Änderungsgrund. Steuerklasse und Krankenkasse können ebenfalls erfasst werden.",
        tips: [
          "DATEV-Import ermöglicht automatische Übernahme",
          "Alle Beträge in Euro"
        ]
      },
      {
        title: "Benefits & Zusatzleistungen",
        content: "Verwaltung von Zusatzleistungen wie Firmenwagen, Jobticket, betriebliche Altersvorsorge, Essenszuschuss etc. Mit Start- und Enddatum sowie monatlichem Wert."
      },
      {
        title: "Bonuszahlungen",
        content: "Erfassung von Einmalzahlungen und Boni: Weihnachtsgeld, Prämien, Provisionen mit Datum, Betrag und Beschreibung."
      },
      {
        title: "Fahrzeugkosten",
        content: "Zuordnung von Firmenfahrzeugen und deren Kosten: Leasing, Versicherung, Tankkarte (DKV), Wartung. Kosten können anteilig dem Mitarbeiter zugeordnet werden."
      },
      {
        title: "Sonstige Kosten",
        content: "Weitere mitarbeiterbezogene Kosten wie Schulungen, Arbeitskleidung, IT-Ausstattung, Werkzeuge etc."
      },
      {
        title: "Zeiterfassung",
        content: "Monatliche Erfassung von Arbeitsstunden, Urlaubstagen, Krankheitstagen und sonstigen Abwesenheiten.",
        tips: [
          "Fehlzeiten werden für die Stundensatzberechnung berücksichtigt"
        ]
      },
      {
        title: "Arbeitskleidung",
        content: "Konfektionsgrößen für Arbeitskleidung: T-Shirt, Sweatshirt, Fleecejacke, Winterjacke, Hose, Sicherheitsschuhe."
      },
      {
        title: "Werkzeuge & Geräte",
        content: "Zugewiesene Werkzeuge und Geräte mit Seriennummer, Übergabedatum und Wiederbeschaffungswert. Unterscheidung zwischen Handwerkzeugen und Maschinen.",
        tips: [
          "Werkzeugliste kann als PDF exportiert werden",
          "Seriennummern für Rückverfolgbarkeit"
        ]
      },
      {
        title: "Zugangsdaten",
        content: "Externe Zugangsdaten für Systeme wie Baustellenportale, Lieferanten-Accounts etc. (verschlüsselt gespeichert)."
      },
      {
        title: "Dokumente",
        content: "Dokumentenablage für Arbeitsverträge, Zeugnisse, Bescheinigungen, Führerscheine etc. mit Kategorisierung.",
        tips: [
          "Unterstützte Formate: PDF, JPG, PNG",
          "Dokumente können heruntergeladen werden"
        ]
      },
      {
        title: "Bewertungen",
        content: "Monatliche Leistungsbewertungen nach definierten Kriterien. Jedes Kriterium wird einzeln bewertet und kann kommentiert werden.",
        tips: [
          "Bewertungen können als PDF exportiert werden",
          "Historie aller Bewertungen verfügbar"
        ]
      }
    ]
  },
  {
    id: "applicants",
    title: "Bewerber",
    icon: <UserPlus className="h-5 w-5" />,
    description: "Bewerbermanagement von der Bewerbung bis zur Einstellung.",
    subsections: [
      {
        title: "Bewerber-Pipeline",
        content: "Kanban-Board mit allen Bewerbungsphasen: Neu, Erstkontakt, Telefoninterview, Vorstellungsgespräch, Probearbeiten, Angebot, Eingestellt, Abgelehnt.",
        tips: [
          "Bewerber per Drag & Drop zwischen Phasen verschieben",
          "Jeder Phasenwechsel wird protokolliert"
        ]
      },
      {
        title: "Bewerberdaten",
        content: "Erfassung aller relevanten Daten: Kontaktdaten, gewünschte Position, Gehaltsvorstellung, frühester Eintrittstermin, Qualifikationen, Führerschein."
      },
      {
        title: "Dokumente",
        content: "Upload von Lebenslauf, Anschreiben, Zeugnissen und weiteren Bewerbungsunterlagen."
      },
      {
        title: "Bewertung & Notizen",
        content: "Interne Bewertung (Sterne) und Notizen zum Bewerbungsprozess. Interviewnotizen können separat erfasst werden."
      },
      {
        title: "Übernahme als Mitarbeiter",
        content: "Bei erfolgreicher Einstellung kann der Bewerber mit allen Daten als Mitarbeiter übernommen werden.",
        tips: [
          "Stammdaten werden automatisch übertragen",
          "Dokumente bleiben erhalten"
        ]
      },
      {
        title: "Karriereseite",
        content: "Konfigurierbare Landingpage für Online-Bewerbungen. Logo, Farben und Texte können angepasst werden."
      }
    ]
  },
  {
    id: "orgchart",
    title: "Organigramm",
    icon: <Network className="h-5 w-5" />,
    description: "Visuelle Darstellung der Unternehmensstruktur und Hierarchien.",
    subsections: [
      {
        title: "Hierarchie-Ansicht",
        content: "Baumdarstellung der Unternehmensstruktur basierend auf den direkten Vorgesetzten der Mitarbeiter.",
        tips: [
          "Vorgesetzte werden in den Mitarbeiter-Stammdaten festgelegt"
        ]
      },
      {
        title: "Abteilungsansicht",
        content: "Gruppierung der Mitarbeiter nach Abteilungen für eine übersichtliche Darstellung."
      },
      {
        title: "Freihand-Modus",
        content: "Flexible Positionierung von Mitarbeiter-Karten für individuelle Organigramm-Layouts."
      },
      {
        title: "Mitarbeiter-Karten",
        content: "Jede Karte zeigt Profilbild, Name, Position und Abteilung. Klick öffnet die Mitarbeiterdetails."
      }
    ]
  },
  {
    id: "costoverview",
    title: "Einstandslöhne",
    icon: <Calculator className="h-5 w-5" />,
    description: "Berechnung der tatsächlichen Mitarbeiterkosten und Stundensätze.",
    subsections: [
      {
        title: "Kostenübersicht",
        content: "Auflistung aller Mitarbeiter mit ihren berechneten Stundensätzen, Bruttogehalt und Gesamtkosten.",
        tips: [
          "Sortierung nach verschiedenen Kriterien möglich"
        ]
      },
      {
        title: "Stundensatz-Berechnung",
        content: "Der Einstandslohn berücksichtigt: Bruttogehalt, Arbeitgeberanteile (Sozialversicherung), Umlagen, Fahrzeugkosten, Werkzeuge, Arbeitskleidung, Schulungen und Overhead.",
        tips: [
          "Formel und Faktoren in den Einstellungen konfigurierbar"
        ]
      },
      {
        title: "Produktive Tage",
        content: "Berechnung der tatsächlich produktiven Arbeitstage unter Berücksichtigung von Urlaub, Feiertagen und geschätzten Krankheitstagen."
      },
      {
        title: "Kostenhistorie",
        content: "Speicherung vergangener Berechnungen für Vergleiche und Kostenentwicklung."
      }
    ]
  },
  {
    id: "matrix",
    title: "Bewertungsmatrix",
    icon: <Grid3x3 className="h-5 w-5" />,
    description: "Übersicht aller Mitarbeiterbewertungen im Zeitverlauf.",
    subsections: [
      {
        title: "Matrix-Ansicht",
        content: "Tabellarische Darstellung aller Mitarbeiter und ihrer monatlichen Bewertungen. Farbcodierung zeigt Leistungsniveau auf einen Blick.",
        tips: [
          "Grün = Voll erfüllt, Gelb = Erfüllt, Orange = Teilweise, Rot = Nicht erfüllt"
        ]
      },
      {
        title: "Filteroptionen",
        content: "Filterung nach Zeitraum, Abteilung oder einzelnen Mitarbeitern."
      },
      {
        title: "Vergleichsansicht",
        content: "Direkter Vergleich mehrerer Mitarbeiter über einen gewählten Zeitraum."
      },
      {
        title: "Export",
        content: "Die Matrix kann als PDF oder Excel exportiert werden."
      }
    ]
  },
  {
    id: "settings",
    title: "Einstellungen",
    icon: <Settings className="h-5 w-5" />,
    description: "Systemkonfiguration und Verwaltungseinstellungen.",
    subsections: [
      {
        title: "Benutzer",
        content: "Verwaltung der App-Benutzer mit Rollen: Admin (Vollzugriff), Personalmanager (HR-Operationen), Personalverwalter (operative Daten), Mitarbeiter (nur eigene Daten).",
        tips: [
          "Neue Benutzer erhalten eine Einladungs-E-Mail",
          "Rollen können jederzeit geändert werden"
        ]
      },
      {
        title: "Benachrichtigungen",
        content: "Konfiguration von E-Mail-Benachrichtigungen bei wichtigen Ereignissen wie neuen Bewerbungen oder Datenänderungen."
      },
      {
        title: "Import",
        content: "CSV-Import für Mitarbeiterdaten. Vorlage kann heruntergeladen werden.",
        tips: [
          "Spalten müssen dem erwarteten Format entsprechen",
          "Vorschau vor dem endgültigen Import"
        ]
      },
      {
        title: "DATEV Import",
        content: "Import von Gehaltsdaten aus DATEV-Abrechnungen. Automatische Erkennung von Mitarbeitern und Zuordnung der Gehaltsinformationen.",
        tips: [
          "PDF-Abrechnungen werden per KI ausgelesen",
          "Manuelle Korrektur vor Übernahme möglich"
        ]
      },
      {
        title: "Firma",
        content: "Firmenstammdaten: Name, Adresse, Kontaktdaten und Firmenlogo. Diese Daten erscheinen auf allen PDF-Exporten.",
        tips: [
          "Logo wird oben links auf PDFs platziert",
          "Adresse erscheint im Fußbereich"
        ]
      },
      {
        title: "Abteilungen",
        content: "Definition der Unternehmensabteilungen. Hierarchische Struktur mit Unter-Abteilungen möglich."
      },
      {
        title: "Stellen",
        content: "Verwaltung der Jobpositionen/Stellenbezeichnungen die Mitarbeitern zugewiesen werden können."
      },
      {
        title: "Fahrzeuge",
        content: "Fuhrparkverwaltung: Fahrzeugdaten, Kennzeichen, Leasing-/Versicherungskosten, TÜV-Termine, DKV-Karten.",
        tips: [
          "DKV-Rechnungen können importiert werden",
          "Automatische Kostenzuordnung zu Mitarbeitern"
        ]
      },
      {
        title: "Kostenarten",
        content: "Definition von Kostenkategorien für die Erfassung sonstiger Mitarbeiterkosten (einmalig oder wiederkehrend)."
      },
      {
        title: "Werkzeuge",
        content: "Werkzeugdefinitionen mit Standardkosten. Unterscheidung zwischen Handwerkzeugen und Maschinen/Messgeräten."
      },
      {
        title: "Einstandslohn",
        content: "Konfiguration der Stundensatz-Berechnung: Sozialversicherungssätze, Umlagen, Arbeitstage, Overhead-Prozentsätze, Ziel-Marge.",
        tips: [
          "Standardwerte entsprechen aktuellen gesetzlichen Vorgaben",
          "Anpassung an Ihre Kalkulation möglich"
        ]
      },
      {
        title: "Kriterien",
        content: "Definition der Bewertungskriterien für Mitarbeiter-Evaluationen. Jedes Kriterium hat einen Namen, eine Gewichtung und mögliche Bewertungsstufen."
      },
      {
        title: "Schwellenwerte",
        content: "Festlegung der Prozent-Schwellenwerte für Leistungsniveaus (z.B. ab 90% = 'Voll erfüllt')."
      },
      {
        title: "Dokumentvorlagen",
        content: "Verwaltung und Bearbeitung von Dokumentvorlagen für verschiedene Beschäftigungsverhältnisse: Vollzeit, Teilzeit, Azubi, Minijob und Werkstudent. Zusätzlich Vorlagen für Vertragsänderungen, Abmahnungen, Zeugnisse und Jubiläumsurkunden.",
        tips: [
          "Vorlagen können mit Platzhaltern wie {mitarbeiter_name} oder {position} personalisiert werden",
          "Vorlagen können dupliziert und als neue Vorlage gespeichert werden",
          "Vorschau-Funktion zeigt die bearbeitete Vorlage vor dem Speichern",
          "Jede Vorlage kann für verschiedene Beschäftigungsarten angepasst werden"
        ]
      },
      {
        title: "Recruiting",
        content: "Konfiguration der Karriereseite für Online-Bewerbungen sowie E-Mail-Vorlagen für die Bewerberkommunikation.",
        tips: [
          "Karriereseite kann mit eigenem Logo und Farben gestaltet werden",
          "E-Mail-Vorlagen für Einladungen, Angebote und Absagen anpassbar",
          "Automatischer DSGVO-Hinweis bei Absagen"
        ]
      },
      {
        title: "Versionshistorie",
        content: "Übersicht aller App-Versionen mit Änderungsprotokoll. Neue Versionen können hier dokumentiert werden.",
        tips: [
          "Klicken Sie auf das Versions-Icon in der Seitenleiste für die aktuelle Version",
          "Änderungen werden nach Kategorie (Feature, Bugfix, Verbesserung) sortiert"
        ]
      }
    ]
  }
];

const HelpPage = () => {
  const [expandedSections, setExpandedSections] = useState<string[]>(["my-profile"]);
  const { userRole } = useAuth();
  
  // Show employee-specific help for employees, full help for others
  const isEmployee = userRole === 'employee';
  const helpSections = isEmployee ? employeeHelpSections : adminHelpSections;

  return (
    <div className="container mx-auto px-3 sm:px-4 py-6 sm:py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-primary text-primary-foreground">
              <HelpCircle className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground">
                {isEmployee ? "Hilfe zu Ihrem Profil" : "Hilfe & Dokumentation"}
              </h1>
              <p className="text-muted-foreground">
                {isEmployee 
                  ? "Erfahren Sie alles über Ihre persönlichen Daten und Funktionen" 
                  : "Erfahren Sie alles über HR-flash Dashboard"}
              </p>
            </div>
          </div>
        </div>

        {/* Quick Info Banner */}
        <Card className="mb-8 border-primary/30 bg-primary/5">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-foreground">
                  {isEmployee ? "Willkommen in Ihrem Mitarbeiterbereich!" : "Willkommen bei HR-flash!"}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  {isEmployee 
                    ? "Hier finden Sie alle Informationen zu Ihrem Profil. Sie können Ihre Arbeitskleidungsgrößen selbst pflegen und Ihre Daten einsehen. Für Änderungen an anderen Daten wenden Sie sich bitte an die Personalabteilung." 
                    : "HR-flash ist Ihre zentrale Plattform für Mitarbeiterverwaltung, Bewerbermanagement und Leistungsbewertung. Navigieren Sie durch die Abschnitte unten, um mehr über die einzelnen Bereiche zu erfahren."}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Help Sections */}
        <div className="grid gap-4">
          {helpSections.map((section) => (
            <Card key={section.id} className="overflow-hidden">
              <Accordion 
                type="multiple" 
                value={expandedSections}
                onValueChange={setExpandedSections}
              >
                <AccordionItem value={section.id} className="border-none">
                  <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-secondary/30">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10 text-primary">
                        {section.icon}
                      </div>
                      <div className="text-left">
                        <h3 className="text-lg font-semibold text-foreground">{section.title}</h3>
                        <p className="text-sm text-muted-foreground font-normal">{section.description}</p>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-6">
                    <div className="grid gap-4 mt-2">
                      {section.subsections.map((subsection, index) => (
                        <div 
                          key={index} 
                          className="p-4 rounded-lg bg-secondary/20 border border-border/50"
                        >
                          <div className="flex items-start gap-3">
                            <CheckCircle2 className="h-5 w-5 text-success mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                              <h4 className="font-semibold text-foreground mb-1">{subsection.title}</h4>
                              <p className="text-sm text-muted-foreground">{subsection.content}</p>
                              {subsection.tips && subsection.tips.length > 0 && (
                                <div className="mt-3 space-y-1">
                                  {subsection.tips.map((tip, tipIndex) => (
                                    <div key={tipIndex} className="flex items-center gap-2 text-xs">
                                      <Zap className="h-3 w-3 text-warning flex-shrink-0" />
                                      <span className="text-muted-foreground">{tip}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </Card>
          ))}
        </div>

        {/* Footer */}
        <Card className="mt-8">
          <CardContent className="py-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-center sm:text-left">
                <p className="text-sm text-muted-foreground">
                  {isEmployee 
                    ? "Fragen zu Ihrem Profil? Wenden Sie sich an die Personalabteilung." 
                    : "Benötigen Sie weitere Hilfe? Kontaktieren Sie uns."}
                </p>
              </div>
              <Badge variant="outline" className="text-xs">
                Version 1.0
              </Badge>
            </div>
          </CardContent>
        </Card>
    </div>
  );
};

export default HelpPage;
